import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Hls from "hls.js";
import {
  Box,
  IconButton,
  Typography,
  CircularProgress
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function StudentWatch() {
  const [params] = useSearchParams();
  const rawUrl = params.get("url");
  const title = params.get("title") || "Video";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const playerRef = useRef(null);

  const isAndroidTV = () =>
    navigator.userAgent.includes("TV") ||
    navigator.userAgent.includes("AndroidTV") ||
    navigator.userAgent.includes("VrPlayApp");

  const extractYoutubeId = (url) => {
    if (!url) return null;

    if (url.includes("youtu.be/")) {
      return url.split("youtu.be/")[1].split("?")[0];
    }
    if (url.includes("watch?v=")) {
      return url.split("watch?v=")[1].split("&")[0];
    }
    return null;
  };

  const youtubeId = extractYoutubeId(rawUrl);
  const isYouTube = !!youtubeId;
  const isHLS = rawUrl?.endsWith(".m3u8");
  const isMP4 = rawUrl?.endsWith(".mp4");

  const goFullscreen = (element) => {
    if (!element) return;
    try {
      if (element.requestFullscreen) element.requestFullscreen();
      else if (element.webkitRequestFullscreen) element.webkitRequestFullscreen();
    } catch (err) {
      console.log("Fullscreen error:", err);
    }
  };

  // ---------------------------------------------
  // Inject YouTube Iframe API Script Once
  // ---------------------------------------------
  const loadYouTubeAPI = () => {
    return new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://www.youtube.com/iframe_api";
      script.onload = () => resolve();
      document.body.appendChild(script);
    });
  };

  // ---------------------------------------------
  // Setup YouTube Player
  // ---------------------------------------------
  useEffect(() => {
    if (!isYouTube) return;

    let ytPlayer;

    loadYouTubeAPI().then(() => {
      window.YT.ready(() => {
        ytPlayer = new window.YT.Player("youtube-player", {
          height: "100%",
          width: "100%",
          videoId: youtubeId,
          playerVars: {
            autoplay: 1,
            controls: 1,
            modestbranding: 1,
            rel: 0,
            fs: 1,
            playsinline: 1
          },
          events: {
            onReady: (event) => {
              setLoading(false);

              const iframe = event.target.getIframe();
              setTimeout(() => {
                if (isAndroidTV()) goFullscreen(iframe);
              }, 500);
            },
            onStateChange: (event) => {
              if (event.data === window.YT.PlayerState.PLAYING) {
                setLoading(false);
              }
            }
          }
        });

        playerRef.current = ytPlayer;
      });
    });

    return () => {
      if (ytPlayer && ytPlayer.destroy) ytPlayer.destroy();
    };
  }, [youtubeId]);

  // ---------------------------------------------
  // HLS / MP4 PLAYER SETUP
  // ---------------------------------------------
  useEffect(() => {
    if (isYouTube) return;

    const video = document.getElementById("html5-video");

    if (!video) return;

    if (isHLS && Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(rawUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play();
        setLoading(false);
        if (isAndroidTV()) goFullscreen(video);
      });

      return () => hls.destroy();
    } else if (isMP4) {
      video.src = rawUrl;
      video.onloadeddata = () => {
        setLoading(false);
        if (isAndroidTV()) goFullscreen(video);
      };
    }
  }, [rawUrl]);

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        background: "#000",
        position: "relative",
        overflow: "hidden"
      }}
    >

      {/* LOADING SPINNER */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: "45vh",
            left: "50%",
            transform: "translateX(-50%)",
            color: "white"
          }}
        >
          <CircularProgress sx={{ color: "white" }} />
        </Box>
      )}

      {/* ----------------------------------- */}
      {/* YOUTUBE IFRAME PLAYER */}
      {/* ----------------------------------- */}
      {isYouTube ? (
        <div
          id="youtube-player"
          style={{
            width: "100%",
            height: "100%",
            background: "black"
          }}
        ></div>
      ) : (
        /* ----------------------------------- */
        /* HLS / MP4 PLAYER */
        /* ----------------------------------- */
        <video
          id="html5-video"
          controls
          autoPlay
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            background: "black"
          }}
        />
      )}
    </Box>
  );
}
